import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Breadcrumb from '../../../../components/Breadcrumb';
import Page from '../../../../components/Page';
import PrimaryButton from '../../../../components/PrimaryButton';
import client from '../../../../lib/feathers';

const CAMPAIGN_TYPES = [
  { value: 'claim_listing', label: 'Claim Listing' },
  { value: 'access_pass', label: 'Access Pass' },
  { value: 'staff_access', label: 'Staff Access' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'custom', label: 'Custom' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'api_existing', label: 'API Existing' },
  { value: 'crawl_cache', label: 'Crawl Cache' },
  { value: 'brave_search', label: 'Brave Search' },
  { value: 'google_places', label: 'Google Places' },
  { value: 'manual', label: 'Manual' },
];

const CLAIMED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Claimed only' },
  { value: 'false', label: 'Unclaimed only' },
];

const EMPTY_SEGMENT = {
  source: '',
  region: '',
  claimed: '',
  featureLevel: '',
  hasWebsite: false,
  hasCheckins: false,
  minCheckins: '',
  hasWines: false,
  hasUnreviewedWines: false,
  addedToList: false,
  lastContactedBefore: '',
};

const EMPTY_FORM = {
  name: '',
  type: '',
  subject: '',
  templateId: '',
  fromEmail: '',
  fromName: 'VinoSeeker',
  scheduledAt: '',
};

function buildSegmentPayload(segment) {
  const payload = {};
  if (segment.source) payload.source = segment.source;
  if (segment.region) payload.region = segment.region;
  if (segment.claimed !== '') payload.claimed = segment.claimed === 'true';
  if (segment.featureLevel !== '') payload.featureLevel = parseInt(segment.featureLevel, 10);
  if (segment.hasWebsite) payload.hasWebsite = true;
  if (segment.hasCheckins) {
    payload.hasCheckins = true;
    if (segment.minCheckins !== '') payload.minCheckins = parseInt(segment.minCheckins, 10);
  }
  if (segment.hasWines) payload.hasWines = true;
  if (segment.hasUnreviewedWines) payload.hasUnreviewedWines = true;
  if (segment.addedToList) payload.addedToList = true;
  if (segment.lastContactedBefore) payload.lastContactedBefore = segment.lastContactedBefore;
  return payload;
}

function segmentFromApi(apiSegment) {
  if (!apiSegment) return { ...EMPTY_SEGMENT };
  return {
    source: apiSegment.source || '',
    region: apiSegment.region || '',
    claimed: apiSegment.claimed === undefined ? '' : String(apiSegment.claimed),
    featureLevel: apiSegment.featureLevel !== undefined ? String(apiSegment.featureLevel) : '',
    hasWebsite: !!apiSegment.hasWebsite,
    hasCheckins: !!apiSegment.hasCheckins,
    minCheckins: apiSegment.minCheckins !== undefined ? String(apiSegment.minCheckins) : '',
    hasWines: !!apiSegment.hasWines,
    hasUnreviewedWines: !!apiSegment.hasUnreviewedWines,
    addedToList: !!apiSegment.addedToList,
    lastContactedBefore: apiSegment.lastContactedBefore
      ? apiSegment.lastContactedBefore.slice(0, 10)
      : '',
  };
}

function formatDateTimeLocal(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

export default function OutreachCampaignNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEditing = !!editId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [segmentOpen, setSegmentOpen] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [segment, setSegment] = useState({ ...EMPTY_SEGMENT });

  // Load existing campaign when editing
  useEffect(() => {
    if (!isEditing) return;
    const load = async () => {
      try {
        setLoading(true);
        const result = await client.service('admin/outreach-campaign').get(editId);
        setFormData({
          name: result.name || '',
          type: result.type || '',
          subject: result.subject || '',
          templateId: result.templateId || '',
          fromEmail: result.fromEmail || '',
          fromName: result.fromName || 'VinoSeeker',
          scheduledAt: formatDateTimeLocal(result.scheduledAt),
        });
        setSegment(segmentFromApi(result.segment));
        if (result.segment && Object.keys(result.segment).length > 0) {
          setSegmentOpen(true);
        }
      } catch (err) {
        console.error('Error loading campaign:', err);
        toast.error('Failed to load campaign');
        navigate('/admin/outreach/campaigns');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [editId]);

  // Fetch preview count when editing an existing campaign and segment changes
  const fetchPreview = useCallback(async () => {
    if (!isEditing) return;
    try {
      setPreviewLoading(true);
      const result = await client.service('admin/outreach-campaign').get(editId, {
        query: { $action: 'preview' },
      });
      setPreviewCount(result.count ?? result.total ?? null);
    } catch (err) {
      console.error('Preview fetch failed:', err);
      setPreviewCount(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [editId, isEditing]);

  // Debounce preview fetches when segment changes (editing only)
  useEffect(() => {
    if (!isEditing || !segmentOpen) return;
    const timer = setTimeout(() => {
      fetchPreview();
    }, 600);
    return () => clearTimeout(timer);
  }, [segment, isEditing, segmentOpen, fetchPreview]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const setSegmentField = (field, value) => {
    setSegment((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    if (!formData.type) {
      toast.error('Campaign type is required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        subject: formData.subject || undefined,
        templateId: formData.templateId || undefined,
        fromEmail: formData.fromEmail || undefined,
        fromName: formData.fromName || undefined,
        segment: buildSegmentPayload(segment),
        scheduledAt: formData.scheduledAt || undefined,
      };

      let result;
      if (isEditing) {
        result = await client.service('admin/outreach-campaign').patch(editId, payload);
        toast.success('Campaign saved');
      } else {
        result = await client.service('admin/outreach-campaign').create(payload);
        toast.success('Campaign created');
      }
      navigate(`/admin/outreach/campaigns/${result.id}`);
    } catch (err) {
      console.error('Error saving campaign:', err);
      toast.error(err.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm';
  const checkboxClass = 'h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary';

  if (loading) {
    return (
      <Page title="Edit Campaign">
        <div className="py-12 text-center text-sm text-gray-500">Loading campaign...</div>
      </Page>
    );
  }

  return (
    <Page title={isEditing ? 'Edit Campaign' : 'New Campaign'}>
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb
          elements={[
            { name: 'Outreach', href: '/admin/outreach/campaigns' },
            { name: 'Campaigns', href: '/admin/outreach/campaigns' },
            { name: isEditing ? 'Edit Campaign' : 'New Campaign' },
          ]}
        />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">

        {/* Basic Details */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Details</h3>
          <div className="space-y-4">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="e.g. Napa Valley Claim Outreach Q2"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="type"
                  required
                  value={formData.type}
                  onChange={(e) => setField('type', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select a type...</option>
                  {CAMPAIGN_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                Subject Line
              </label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={(e) => setField('subject', e.target.value)}
                placeholder="e.g. Claim your free listing on VinoSeeker"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">The email subject line sent to recipients</p>
            </div>

            <div>
              <label htmlFor="templateId" className="block text-sm font-medium text-gray-700">
                Template ID
              </label>
              <input
                type="text"
                id="templateId"
                value={formData.templateId}
                onChange={(e) => setField('templateId', e.target.value)}
                placeholder="e.g. claim-listing-v2 or 12345678"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">Postmark template ID or alias</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fromEmail" className="block text-sm font-medium text-gray-700">
                  From Email
                </label>
                <input
                  type="email"
                  id="fromEmail"
                  value={formData.fromEmail}
                  onChange={(e) => setField('fromEmail', e.target.value)}
                  placeholder="outreach@vinoseeker.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="fromName" className="block text-sm font-medium text-gray-700">
                  From Name
                </label>
                <input
                  type="text"
                  id="fromName"
                  value={formData.fromName}
                  onChange={(e) => setField('fromName', e.target.value)}
                  placeholder="VinoSeeker"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Segment Filters */}
        <div className="bg-white shadow sm:rounded-lg">
          <button
            type="button"
            onClick={() => setSegmentOpen((v) => !v)}
            className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
          >
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-medium text-gray-900">Segment Filters</h3>
              {Object.values(buildSegmentPayload(segment)).length > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  {Object.keys(buildSegmentPayload(segment)).length} active
                </span>
              )}
            </div>
            <svg
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${segmentOpen ? 'rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {segmentOpen && (
            <div className="px-6 pb-6 space-y-4 border-t border-gray-100">

              {isEditing && (
                <div className="mt-4 flex items-center gap-2">
                  <div className="text-sm text-gray-600">
                    {previewLoading ? (
                      <span className="text-gray-400 italic">Calculating recipients...</span>
                    ) : previewCount !== null ? (
                      <span>
                        <span className="font-semibold text-gray-900">{previewCount.toLocaleString()}</span>
                        {' '}contact{previewCount !== 1 ? 's' : ''} match this segment
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={fetchPreview}
                        className="text-primary hover:text-red-900 text-sm underline"
                      >
                        Preview recipient count
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!isEditing && (
                <p className="mt-4 text-sm text-gray-500">
                  Save the campaign to see a recipient count preview.
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label htmlFor="seg-source" className="block text-sm font-medium text-gray-700">
                    Source
                  </label>
                  <select
                    id="seg-source"
                    value={segment.source}
                    onChange={(e) => setSegmentField('source', e.target.value)}
                    className={inputClass}
                  >
                    {SOURCE_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="seg-claimed" className="block text-sm font-medium text-gray-700">
                    Claimed
                  </label>
                  <select
                    id="seg-claimed"
                    value={segment.claimed}
                    onChange={(e) => setSegmentField('claimed', e.target.value)}
                    className={inputClass}
                  >
                    {CLAIMED_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="seg-region" className="block text-sm font-medium text-gray-700">
                    Region
                  </label>
                  <input
                    type="text"
                    id="seg-region"
                    value={segment.region}
                    onChange={(e) => setSegmentField('region', e.target.value)}
                    placeholder="e.g. California or Napa"
                    className={inputClass}
                  />
                  <p className="mt-1 text-xs text-gray-500">State or city name</p>
                </div>

                <div>
                  <label htmlFor="seg-featureLevel" className="block text-sm font-medium text-gray-700">
                    Feature Level
                  </label>
                  <input
                    type="number"
                    id="seg-featureLevel"
                    min="0"
                    value={segment.featureLevel}
                    onChange={(e) => setSegmentField('featureLevel', e.target.value)}
                    placeholder="Any"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="seg-lastContactedBefore" className="block text-sm font-medium text-gray-700">
                  Last Contacted Before
                </label>
                <input
                  type="date"
                  id="seg-lastContactedBefore"
                  value={segment.lastContactedBefore}
                  onChange={(e) => setSegmentField('lastContactedBefore', e.target.value)}
                  className={`${inputClass} sm:max-w-xs`}
                />
                <p className="mt-1 text-xs text-gray-500">Only include contacts not emailed since this date</p>
              </div>

              <div className="space-y-3 pt-2">
                <p className="text-sm font-medium text-gray-700">Additional Filters</p>

                <div className="flex items-center">
                  <input
                    id="seg-hasWebsite"
                    type="checkbox"
                    checked={segment.hasWebsite}
                    onChange={(e) => setSegmentField('hasWebsite', e.target.checked)}
                    className={checkboxClass}
                  />
                  <label htmlFor="seg-hasWebsite" className="ml-3 text-sm text-gray-700">
                    Has website
                  </label>
                </div>

                <div>
                  <div className="flex items-center">
                    <input
                      id="seg-hasCheckins"
                      type="checkbox"
                      checked={segment.hasCheckins}
                      onChange={(e) => setSegmentField('hasCheckins', e.target.checked)}
                      className={checkboxClass}
                    />
                    <label htmlFor="seg-hasCheckins" className="ml-3 text-sm text-gray-700">
                      Has check-ins
                    </label>
                  </div>
                  {segment.hasCheckins && (
                    <div className="mt-2 ml-7">
                      <label htmlFor="seg-minCheckins" className="block text-sm font-medium text-gray-700">
                        Minimum check-ins
                      </label>
                      <input
                        type="number"
                        id="seg-minCheckins"
                        min="1"
                        value={segment.minCheckins}
                        onChange={(e) => setSegmentField('minCheckins', e.target.value)}
                        placeholder="1"
                        className={`${inputClass} sm:max-w-xs`}
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    id="seg-hasWines"
                    type="checkbox"
                    checked={segment.hasWines}
                    onChange={(e) => setSegmentField('hasWines', e.target.checked)}
                    className={checkboxClass}
                  />
                  <label htmlFor="seg-hasWines" className="ml-3 text-sm text-gray-700">
                    Has wines
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="seg-hasUnreviewedWines"
                    type="checkbox"
                    checked={segment.hasUnreviewedWines}
                    onChange={(e) => setSegmentField('hasUnreviewedWines', e.target.checked)}
                    className={checkboxClass}
                  />
                  <label htmlFor="seg-hasUnreviewedWines" className="ml-3 text-sm text-gray-700">
                    Has unreviewed wines
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    id="seg-addedToList"
                    type="checkbox"
                    checked={segment.addedToList}
                    onChange={(e) => setSegmentField('addedToList', e.target.checked)}
                    className={checkboxClass}
                  />
                  <label htmlFor="seg-addedToList" className="ml-3 text-sm text-gray-700">
                    Added to list
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Schedule */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Schedule</h3>
          <p className="text-sm text-gray-500 mb-4">Optionally schedule this campaign to send at a future date and time.</p>
          <div>
            <label htmlFor="scheduledAt" className="block text-sm font-medium text-gray-700">
              Send At
            </label>
            <input
              type="datetime-local"
              id="scheduledAt"
              value={formData.scheduledAt}
              onChange={(e) => setField('scheduledAt', e.target.value)}
              className={`${inputClass} sm:max-w-xs`}
            />
            <p className="mt-1 text-xs text-gray-500">Leave blank to save as draft without scheduling</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end items-center gap-3 pb-8">
          <button
            type="button"
            onClick={() => navigate('/admin/outreach/campaigns')}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <PrimaryButton type="submit" disabled={saving}>
            {saving
              ? isEditing ? 'Saving...' : 'Creating...'
              : isEditing ? 'Save Campaign' : 'Create Campaign'}
          </PrimaryButton>
        </div>

      </form>
    </Page>
  );
}
